import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ArrowUp } from "lucide-react";
import { cn } from "@/lib/utils";
import { useStickyCart } from "@/contexts/StickyCartContext";

export function ScrollToTop() {
  const [showScrollButton, setShowScrollButton] = useState(false);
  const { isStickyCartVisible, isCartSidebarOpen } = useStickyCart();
  const rafId = useRef<number | null>(null);

  const handleScroll = useCallback(() => {
    // Cancel any pending RAF to prevent stacking
    if (rafId.current !== null) return;
    
    rafId.current = requestAnimationFrame(() => {
      // Batch DOM reads inside RAF
      const mainElement = document.querySelector('main.overflow-y-scroll');
      
      if (mainElement) {
        setShowScrollButton(mainElement.scrollTop > 300);
      } else {
        setShowScrollButton(window.scrollY > 300);
      }
      rafId.current = null;
    });
  }, []);

  useEffect(() => {
    const mainElement = document.querySelector('main.overflow-y-scroll');
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    mainElement?.addEventListener('scroll', handleScroll, { passive: true });
    
    // Initial check
    handleScroll();
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      mainElement?.removeEventListener('scroll', handleScroll);
      if (rafId.current !== null) {
        cancelAnimationFrame(rafId.current);
      }
    };
  }, [handleScroll]);

  const scrollToTop = () => {
    // Check for scroll containers in priority order
    const mainElement = document.querySelector('main.overflow-y-scroll');
    
    if (mainElement) {
      // Mobile: scroll main element
      mainElement.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      // Desktop: scroll window
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  return (
    <Button
      onClick={scrollToTop}
      className={cn(
        "fixed right-6 z-[10000] h-14 w-14 rounded-full shadow-2xl bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300",
        isStickyCartVisible ? "bottom-[calc(5.5rem+3px)]" : "bottom-6",
        showScrollButton && !isCartSidebarOpen
          ? "translate-y-0 opacity-100 scale-100"
          : "translate-y-20 opacity-0 scale-75 pointer-events-none"
      )}
      aria-label="Scroll to top"
    >
      <ArrowUp className="h-6 w-6" />
    </Button>
  );
}
